import type { ReactNode } from "react";
import type React from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { LockClosedIcon, LockOpenIcon } from "../lock-icons";
import type { Composition, CompositionOperator, CriterionRequirement, Operator } from "../design-codec";

export interface ReferenceOption {
  value: string;
  label: string;
}

export const OPERATOR_LABELS: Record<Operator, string> = {
  EQ: "= (Genau)",
  NE: "≠ (Nicht)",
  GE: "≥ (Min)",
  GT: "> (Größer)",
  LE: "≤ (Max)",
  LT: "< (Kleiner)",
};

export const COMPOSITION_LABELS: Record<CompositionOperator, string> = {
  MIN: "Mind. (Anzahl)",
  MAX: "Max. (Anzahl)",
  EXACT: "Genau (Anzahl)",
  ANY: "Beliebige",
  ALL: "Alle",
};

// Per @adr SPARK-0007-match-requirements-mirror-genesis-design@1: spark only offers MIN/MAX/EXACT/ALL. ANY (== MIN 1) and
// AVERAGE (not solver-supported) are deliberately excluded from the picker.
export const COMPOSITION_CHOICES: CompositionOperator[] = ["MIN", "MAX", "EXACT", "ALL"];

export const SELECT_CLASS =
  "h-8 min-w-0 rounded-md ring-1 ring-ink/10 bg-surface px-2 text-[12px] text-ink focus:ring-ink/30 focus:outline-none";
export const INPUT_CLASS =
  "h-8 min-w-0 rounded-md ring-1 ring-ink/10 bg-surface px-2 text-[12px] text-ink focus:ring-ink/30 focus:outline-none";

export function RowShell({
  children,
  onRemove,
  readOnly,
}: {
  children: ReactNode;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const removeLabel = intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" });
  return (
    <div
      data-testid="requirement-row"
      className="flex flex-wrap items-center gap-1.5 py-2"
    >
      {children}
      {!readOnly && onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          data-testid="requirement-row-remove"
          className="ml-auto shrink-0 text-ink/40 hover:text-ink"
          aria-label={removeLabel}
          title={removeLabel}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

export function OperatorSelect({
  value,
  allowed,
  onChange,
  readOnly,
}: {
  value: Operator;
  allowed: Operator[];
  onChange: (next: Operator) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  return (
    <select
      className={SELECT_CLASS}
      value={value}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value as Operator)}
      aria-label={intl.formatMessage({ id: "match.requirements.aria.operator", defaultMessage: "Operator" })}
    >
      {allowed.map((op) => (
        <option key={op} value={op}>
          {OPERATOR_LABELS[op]}
        </option>
      ))}
    </select>
  );
}

export function CompositionEditor({
  value,
  onChange,
  readOnly,
}: {
  value: Composition;
  onChange: (next: Composition) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const needsCount = value.operator !== "ALL";
  return (
    <>
      <span aria-hidden className="shrink-0 text-ink/30">·</span>
      <select
        className={SELECT_CLASS}
        value={value.operator}
        disabled={readOnly}
        onChange={(e) =>
          onChange({
            operator: e.target.value as CompositionOperator,
            value: e.target.value === "ALL" ? null : value.value ?? 1,
          })
        }
        aria-label={intl.formatMessage({ id: "match.requirements.aria.composition", defaultMessage: "Composition" })}
      >
        {COMPOSITION_CHOICES.map((op) => (
          <option key={op} value={op}>
            {COMPOSITION_LABELS[op]}
          </option>
        ))}
      </select>
      {needsCount ? (
        <input
          type="number"
          min={1}
          className={`${INPUT_CLASS} w-16`}
          value={value.value ?? ""}
          disabled={readOnly}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange({ operator: value.operator, value: Number.isFinite(n) && n > 0 ? n : null });
          }}
          aria-label={intl.formatMessage({ id: "match.placeholderCount", defaultMessage: "Anzahl" })}
        />
      ) : null}
    </>
  );
}

export function LookupSelect({
  value,
  options,
  onChange,
  placeholder,
  readOnly,
}: {
  value: string;
  options: ReferenceOption[];
  onChange: (next: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  return (
    <select
      className={SELECT_CLASS}
      value={value}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value)}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Integer value for a person-detail criterion.
 *
 * The attributes are the affordance; the clamp and the ROUND in onChange are the enforcement, because
 * `min`/`max` on an input outside a <form> do not stop typing. The round is the load-bearing part:
 * design-codec types every integer condition as `z.number().int()`, so a fractional value failed
 * safeParse and the whole row vanished into `unknownConditions` on the next load — invisible, not
 * editable and not removable, while still being applied as a real filter by the solver.
 */
export function NumberValueInput({
  value,
  onChange,
  readOnly,
  placeholder,
  min = 0,
  max,
}: {
  value: number;
  onChange: (next: number) => void;
  readOnly?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      step={1}
      min={min}
      max={max}
      className={`${INPUT_CLASS} w-24`}
      value={Number.isFinite(value) ? value : ""}
      disabled={readOnly}
      placeholder={placeholder}
      onChange={(e) => {
        const n = Number(e.target.value);
        /* v8 ignore next 4 -- unreachable through the DOM: a number input value-sanitizes anything
           non-numeric to "", and Number("") is 0. Kept because NaN would flow into the persisted match
           design and fail design-codec's .int(), losing the row — the exact bug this input just fixed. */
        if (!Number.isFinite(n)) {
          onChange(min);
          return;
        }
        const bounded = max === undefined ? Math.max(min, n) : Math.min(max, Math.max(min, n));
        onChange(Math.round(bounded));
      }}
    />
  );
}

export function TextValueInput({
  value,
  onChange,
  readOnly,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      className={`${INPUT_CLASS} w-40 max-w-full`}
      value={value}
      disabled={readOnly}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-8 shrink-0 items-center rounded-md bg-ink/[0.06] px-3 text-[12px] font-medium text-ink">
      {children}
    </span>
  );
}

export function SubsectionHeader({
  title,
  hint,
  right,
}: {
  title: string;
  hint?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 py-2">
      <span className="text-[12px] font-medium text-ink/80">{title}</span>
      {hint ? <span className="ml-2 text-[10px] text-ink/40">{hint}</span> : null}
      {right ? <span className="ml-auto">{right}</span> : null}
    </div>
  );
}

// Muss/Kann als Schloss (Muster PeopleStep-Lock): geschlossen = Muss (hartes Gate, nicht
// erfuellt -> unqualifiziert), offen = Kann (zaehlt ins ungewichtete Score-Mittel).
export function RequirementControls({
  requirement,
  onChange,
  readOnly,
}: {
  requirement: CriterionRequirement;
  onChange: (requirement: CriterionRequirement) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const isMust = requirement === "MUST";
  const label = intl.formatMessage(
    isMust
      ? {
          id: "match.recruiting.must.tooltip",
          defaultMessage: "Muss-Kriterium: wer es nicht erfüllt, ist unqualifiziert. Klicken für Kann.",
        }
      : {
          id: "match.recruiting.kann.tooltip",
          defaultMessage: "Kann-Kriterium: zählt in den Passungs-Score. Klicken für Muss.",
        },
  );
  return (
    <Button
      variant="icon"
      size="sm"
      disabled={readOnly}
      onClick={() => onChange(isMust ? "OPTIONAL" : "MUST")}
      aria-pressed={isMust}
      aria-label={label}
      title={label}
      data-testid="requirement-lock"
      className="shrink-0"
      // Muss: Kreis dauerhaft in Signalfarbe, Schloss in on-accent. Inline-Style wie im
      // ResultPanel-Accent-Muster, damit die hover-Utilities des icon-Buttons nicht
      // druebermalen.
      style={
        isMust
          ? { background: "var(--color-accent)", color: "var(--color-on-accent)" }
          : undefined
      }
    >
      {isMust ? <LockClosedIcon /> : <LockOpenIcon />}
    </Button>
  );
}
